/**
 * TipoCobroBadge - Badge con colores semánticos por tipo de cobro.
 * Usa clases Tailwind con variantes dark: para soporte automático de tema.
 *
 * Detecta pago parcial de dos formas:
 * 1. Via `pagos` (OrdenDetailSheet): tipo_cobro=null + al menos un pago activo
 * 2. Via `totalPagado` (tabla): tipo_cobro=null + total_pagado > 0
 */

import { useTranslation } from "react-i18next";
import type { PagoOrden } from "@/hooks/use-ordenes";

const TIPO_COBRO_CLASSES: Record<string, { pill: string; dot: string }> = {
  SIN_PAGO: {
    pill: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 border border-gray-300 dark:border-gray-600",
    dot: "bg-gray-400 dark:bg-gray-500",
  },
  DIRECTO: {
    pill: "bg-green-100 text-green-800 dark:bg-green-900/60 dark:text-green-200",
    dot: "bg-green-600 dark:bg-green-400",
  },
  FACTURA: {
    pill: "bg-violet-100 text-violet-800 dark:bg-violet-900/60 dark:text-violet-200",
    dot: "bg-violet-600 dark:bg-violet-400",
  },
  PAGO_PARCIAL: {
    pill: "bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200",
    dot: "bg-amber-500 dark:bg-amber-400",
  },
  CORTESIA: {
    pill: "bg-teal-100 text-teal-800 dark:bg-teal-900/60 dark:text-teal-200",
    dot: "bg-teal-600 dark:bg-teal-400",
  },
};

interface TipoCobroBadgeProps {
  tipoCobro: string | null;
  /** Lista de pagos — para detectar pago parcial en OrdenDetailSheet */
  pagos?: Pick<PagoOrden, "is_active">[];
  /** Total pagado como string — para detectar pago parcial en la tabla sin cargar pagos */
  totalPagado?: string;
  /** Si la orden es de cortesía — muestra badge teal "Cortesía" */
  esCortesia?: boolean;
  className?: string;
  /** Contexto para hacer el testid único (ej: "table", "detail-sheet") */
  context?: string;
}

export function TipoCobroBadge({
  tipoCobro,
  pagos,
  totalPagado,
  esCortesia,
  className,
  context,
}: TipoCobroBadgeProps) {
  const { t } = useTranslation();

  // Cortesía tiene prioridad sobre cualquier otro estado
  if (esCortesia) {
    const classes = TIPO_COBRO_CLASSES.CORTESIA;
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${classes.pill} ${className ?? ""}`}
        data-testid={`badge-cortesia${context ? `-${context}` : ""}`}
      >
        <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${classes.dot}`} />
        {t("ordenes.cortesia.badge")}
      </span>
    );
  }

  // Detectar pago parcial:
  // - Via pagos (detail sheet): tipo_cobro=null + al menos un pago activo
  // - Via totalPagado (tabla): tipo_cobro=null + total_pagado > 0
  const tienePagosActivos = pagos ? pagos.some((p) => p.is_active) : false;
  const tieneTotalPagado = totalPagado ? parseFloat(totalPagado) > 0 : false;
  const esPagoParcial = tipoCobro === null && (tienePagosActivos || tieneTotalPagado);

  let key: string;
  if (tipoCobro === "DIRECTO") {
    key = "DIRECTO";
  } else if (tipoCobro === "FACTURA") {
    key = "FACTURA";
  } else if (esPagoParcial) {
    key = "PAGO_PARCIAL";
  } else {
    key = "SIN_PAGO";
  }

  const classes = TIPO_COBRO_CLASSES[key];

  const label =
    key === "DIRECTO"
      ? t("ordenes.tipoCobro.directo")
      : key === "FACTURA"
        ? t("ordenes.tipoCobro.factura")
        : key === "PAGO_PARCIAL"
          ? t("ordenes.tipoCobro.pagoParcial")
          : t("ordenes.tipoCobro.sinPago");

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${classes.pill} ${className ?? ""}`}
      data-testid={
        key === "PAGO_PARCIAL" ? `badge-pago-parcial${context ? `-${context}` : ""}` : undefined
      }
    >
      <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${classes.dot}`} />
      {label}
    </span>
  );
}
